import { ACCESS_ERROR_CODES, accessCodeKey, ApiError, isApiError, messageKey, toApiError } from "../data/errors";

export { ACCESS_ERROR_CODES };

/**
 * Translation key (under `next.`) for an access refusal. A 5A code gets its own sentence; a bare 403 (a policy with no
 * code) is "not allowed"; any other 4xx gets the generic line (the server's text is English, never shown); network and
 * 5xx fall back to `messageKey`.
 */
export const errorMessageKey = (e: ApiError): string => {
  const coded = accessCodeKey(e.code);
  if (coded) return coded;
  if (e.status === 403 && !e.code) return "access.errors.not_allowed";
  if (e.kind === "client") return "errors.generic";
  return messageKey(e) ?? "errors.generic";
};

type T = (key: string, opts?: Record<string, unknown>) => string;

/**
 * ACCESS_CHANGED says who changed it: `changed_by.name` when the server sends one, "ConnectYid staff" when it sends
 * the person as staff without a name; no one named (null) keeps the plain sentence.
 */
const accessChangedKey = (e: ApiError): { key: string; name?: string } | null => {
  const by = (e.raw as { response?: { data?: { changed_by?: { name?: unknown; staff?: unknown } | null } } })?.response?.data?.changed_by;
  if (!by || typeof by !== "object") return null;
  if (typeof by.name === "string" && by.name.trim()) return { key: "access.errors.access_changed_by", name: by.name.trim() };
  return by.staff ? { key: "access.errors.access_changed_staff" } : null;
};

/** The sentence to show for any thrown value. */
export const errorText = (err: unknown, t: T): string => {
  const e = isApiError(err) ? err : toApiError(err);
  const who = e.code === "ACCESS_CHANGED" ? accessChangedKey(e) : null;
  return who ? t(who.key, { name: who.name }) : t(errorMessageKey(e));
};
